'use strict';

const querystring = require('querystring');
const { getCookieValue } = require('../../lib/util');
const basicAuth = require('../../lib/basicAuth');
const util = require('../../lib/util.js');
const performanceMeasure = require('../../lib/performanceMeasure.js');
const callbackQueuePromise = require('../../lib/callbackQueuePromise');
const clientConfigPromise = callbackQueuePromise('phxConfigQueue');
let measure = performanceMeasure('webcomponents:page');
/**
 * PhoenixElement
 *
 * Abstract base class used by many Phoenix custom elements.
 */
export class PhoenixElement extends HTMLElement {
    /**
     * Read and parse the JSON value of the "config" attribute.
     *
     * Note: this attribute is not watched for change.
     */
    get config() {
        return this._config || (this._config = this.getAttr('config', { parseJson: true }));
    }

    /**
     * Read and parse the document's query string value into an object.
     *
     * Note: this attribute is not watched for change.
     */
    get query() {
        return querystring.parse(document.location.search.slice(1));
    }

    /**
     * getAttr is a wrapper around getAttribute adding validation and parsing options.
     *
     * @param {string} attrName - Name of the element attribute.
     * @param {object} options - See below.
     * @return {*} - Value of the element attribute.
     *
     * Options:
     * - required: true, throw an error if the value is not "thruthy".
     * - parseJson: true, if the value is a string, parse it as JSON.
     *
     * Note, for HTML5 boolean attributes, you may just use the following:
     *   this.hasAttribute(attrName);
     *
     * @example
     *   this._adConfig = this.getAttr('ad-config', { required: true, parseJson:true })';
     *   this._id = this.getAttr('id', { required: true });
     *
     */
    getAttr(attrName, options = {}) {
        let value = this.getAttribute(attrName);
        if (options.required) {
            if (!value) {
                throw new Error('attribute ' + attrName + ' is required for element ' + this.tagName);
            }
        }
        if (options.parseJson) {
            if (typeof value === 'string') {
                value = JSON.parse(value);
            }
        }
        return value;
    }

    connectedCallback() {
        /**
         * observedEmittedEvents is a getter that returns an array of all the custom
         * emitted event names the component likes to observe
         *
         * Example: get observedEmittedEvents() { return ['event1', 'event2']; }
         */
        if (this.observedEmittedEvents) {
            if (!this.emittedEventCallback) {
                throw new Error('emittedEventCallback is required for element ' + this.tagName);
            }
            this._addEmittedEventsListeners();
        }

        /**
         * observedBroadcastedEvents is a getter that returns an array of all the
         * custom broadcasted event names the component likes to observe
         *
         * Example: get observedEmittedEvents() { return ['event1', 'event2']; }
         */
        if (this.observedBroadcastedEvents) {
            if (!this.broadcastedEventCallback) {
                throw new Error('broadcastedEventCallback is required for element ' + this.tagName);
            }
            this._addBroadcastedEventsListeners();
        }
    }

    /**
     * _addEmittedEventsListeners creates event listeners for all the observed emitted events
     */
    _addEmittedEventsListeners() {
        this.observedEmittedEvents.forEach((ev) => {
            this.addEventListener(ev, this._emittedEventCallback);
        });
    }

    /**
     * _removeEmittedEventsListeners remove event listeners for all the observed emitted events
     */
    _removeEmittedEventsListeners() {
        if (this.observedEmittedEvents) {
            this.observedEmittedEvents.forEach((ev) => {
                this.removeEventListener(ev, this._emittedEventCallback);
            });
        }
    }
    /**
     * _emittedEventCallback is a callback to stop event propogation once the emitted event is captured
     * and to call emittedEventCallback subclass
     */
    _emittedEventCallback(e) {
        e.stopPropagation();
        this.emittedEventCallback(e.type, e);
    }

    /**
     * _addBroadcastedEventsListeners creates event listeners for all the observed broadcasted events
     */
    _addBroadcastedEventsListeners() {
        this.observedBroadcastedEvents.forEach((ev) => {
            this.emitEvent('register:broadcastListener', {
                bubbles: true,
                detail: {
                    type: ev,
                    callback: this.broadcastedEventCallback.bind(this),
                },
            });
        });
    }

    /**
     * _removeBroadcastedEventsListeners removes event listeners for all the observed broadcasted events
     */
    _removeBroadcastedEventsListeners() {
        if (this.observedBroadcastedEvents) {
            this.observedBroadcastedEvents.forEach((ev) => {
                this.emitEvent('unregister:broadcastListener', {
                    bubbles: true,
                    detail: {
                        type: ev,
                        callback: this.broadcastedEventCallback.bind(this),
                    },
                });
            });
        }
    }

    /**
     * emitEvent is a wrapper around Custom Event dispatcher.
     *
     * @param {string} eventName - Name of the custom event.
     * @param {CustomEventInit} customEventInit - https://developer.mozilla.org/en-US/docs/Web/API/CustomEvent/CustomEvent.
     *
     * @example
     *   this.emitEvent('doSomething', { bubble: true, detail: {....} });
     *
     */
    emitEvent(eventName, customEventInit = {}) {
        if (eventName) {
            this.dispatchEvent(new CustomEvent(eventName, customEventInit));
        }
    }

    /**
     * Shortcut for util.waitForElement(this, query, options).
     * Use directly util.waitForElement to query from a rootElement that is not "this".
     */
    waitForElement(query, options) {
        return util.waitForElement(this, query, options);
    }

    /**
     * makeQueryablePromise is warapper around promises to make promise queryable for its state
     *
     * @param {*} promise
     */
    makeQueryablePromise(promise) {
        // Don't create a wrapper for promises that can already be queried.
        if (promise.isResolved) return promise;

        var isResolved = false;
        var isRejected = false;

        // Observe the promise, saving the fulfillment in a closure scope.
        var result = promise.then(
            function (v) {
                isResolved = true;
                return v;
            },
            function (e) {
                isRejected = true;
                throw e;
            }
        );
        result.isFulfilled = function () {
            return isResolved || isRejected;
        };
        result.isResolved = function () {
            return isResolved;
        };
        result.isRejected = function () {
            return isRejected;
        };
        return result;
    }

    /**
     * observeFirstIntersection is a wrapper around util.observeFirstIntersection.
     * onFirstIntersection is called at the first observation.
     *
     * It may be necessary for the subclass to call super.disconnectedCallback() in order to properly
     * unobserve the element when it is disconnected from the DOM.
     *
     * @param {string} rootMargin - the IntersectionObserver rootMargin, defaults to '190px 0px'.
     */
    observeFirstIntersection(rootMargin) {
        if (this._firstIntersectionObserver) {
            // There are cases, on some browsers where connectedCallback is called twice.
            // See https://bugs.chromium.org/p/chromium/issues/detail?id=821195&desc=2
            // It seems to work because disconnedtedCallback is called between the two calls to
            // connectedCallback. This warning is to detect cases of double call without
            // disconnedtedCallback in between.
            // eslint-disable-next-line no-console
            console.warn('already observed.');
        }
        this._firstIntersectionObserver = util.observeFirstIntersection(this, rootMargin);
    }

    /**
     * onFirstIntersection must be subclassed if observeFirstIntersection() is called.
     */
    onFirstIntersection() {
        throw new Error('must be subclassed');
    }

    /**
     * onAfterFirstFullView must be subclassed if observeAfterFirstFullView() is called.
     */
    onAfterFirstFullView() {
        throw new Error('must be subclassed');
    }

    /**
     * unobserveFirstIntersection stops observing the first intersection.
     * onFirstIntersection won't be called after this.
     */
    unobserveFirstIntersection() {
        if (this._firstIntersectionObserver) {
            this._firstIntersectionObserver.unobserve(this);
            this._firstIntersectionObserver = undefined; // let the element be observed again.
        }
    }

    /**
     * observeVisibilityChanges is a wrapper around util.observeVisibilityChanges.
     * onChangeToVisible and onChangeToNotVisible are called when the visiblity
     * crosses 50%.
     *
     * It may be necessary for the subclass to call super.disconnectedCallback() in order to properly
     * unobserve the element when it is disconnected from the DOM.
     */
    observeVisibilityChanges() {
        if (this._visiblityObserver) {
            // Same issue with connectedCallback being called twice. See above.
            // eslint-disable-next-line no-console
            console.warn('already observed.');
        }
        this._visiblityObserver = util.observeVisibilityChanges(this);
    }

    observeAfterFirstFullView() {
        if (this._afterFirstViewObserver) {
            // Same issue with connectedCallback being called twice. See above.
            // eslint-disable-next-line no-console
            console.warn('already observed.');
        }
        this._afterFirstViewObserver = util.observeAfterFirstFullView(this);
    }

    /**
     * unobserveVisibilityChanges stops observing the visibility changes.
     * onChangeToVisible and onChangeToNotVisible won't be called after this.
     */
    unobserveVisibilityChanges() {
        if (this._visiblityObserver) {
            this._visiblityObserver.unobserve(this);
            this._visiblityObserver = undefined; // let the element be observed again.
        }
    }

    /**
     * onChangeToVisible must be subclassed if observeVisibilityChanges() is called.
     */
    onChangeToVisible() {
        throw new Error('must be subclassed');
    }

    /**
     * onChangeToNotVisible must be subclassed if observeVisibilityChanges() is called.
     */
    onChangeToNotVisible() {
        throw new Error('must be subclassed');
    }

    /**
     * disconnectedCallback must be invoked with super if the subclass redefines this method:
     *   disconnectedCallback() {
     *     super.disconnectedCallback();
     *     ...
     *   }
     */
    disconnectedCallback() {
        this.unobserveFirstIntersection();
        this.unobserveVisibilityChanges();
        this._removeEmittedEventsListeners();
        this._removeBroadcastedEventsListeners();
    }

    /**
     * Returns a promise with a boolean result as to whether the component is
     * allowed to render based on the following supported attributes/rules:
     *
     * block-premium: When present, this attribute will direct canRender() to
     *   yield a false result when the active user is a Tempest Premium subscriber.
     * block-on-gdpr-applicable: When present, this attribute will direct canRender() to
     *   yield a false result when the active user is from an EEA country.
     * block-on-gdpr-vendor-consent: When present, this attribute will direct canRender()
     *   to yield a false result when GDPR applies and when the user has not consented
     *   to the specified vendor.
     * block-on-gdpr-purpose-consent: When present, this attribute will direct canRender()
     *   to yield a false result when GDPR applies and when the user has not consented
     *   to the specified purpose(s) (a comma-delimited list is supported).
     * block-on-gpp-reject: When present, this attribute will direct canRender() to yield
     *   a false result when GPP applies and the user has rejected the GPP consent.
     * block-on-demonetized: When present, this attribute will direct canRender() to yield
     *   a false result when the UID service directs to demonetize the client.
     */
    canRender() {
        const blockPremium = this.hasAttribute('block-premium');
        const blockOnGdprApplicable = this.hasAttribute('block-on-gdpr-applicable');
        const gdprConsentVendor = this.getAttribute('block-on-gdpr-vendor-consent');
        const blockGppReject = this.hasAttribute('block-on-gpp-reject');
        const blockDemonetized = this.hasAttribute('block-on-demonetized');

        let purposes = this.getAttribute('block-on-gdpr-purpose-consent');
        let gdprConsentPurposes;
        if (purposes) {
            gdprConsentPurposes = purposes.split(',');
        }
        const blockGdprConsent = gdprConsentVendor || gdprConsentPurposes;

        const promises = [];
        if (blockPremium) {
            promises.push(util.userHasPremium().then((hasPremium) => !hasPremium));
        }

        const gdpr = window.GdprStatus;
        if (blockOnGdprApplicable) {
            promises.push(gdpr.isApplicableSafe().then((isApplicable) => !isApplicable));
        }

        if (blockDemonetized) {
            promises.push(gdpr.isDemonetizedSafe().then((demonetized) => !demonetized));
        }

        if (blockGdprConsent) {
            promises.push(
                gdpr.isApplicableSafe().then((isApplicable) => {
                    if (isApplicable) {
                        return gdpr.waitForConsentValue().then(() => {
                            if (gdprConsentVendor) {
                                return gdpr.isConsentGivenForVendorPurposes(
                                    gdprConsentVendor,
                                    gdprConsentPurposes
                                );
                            } else {
                                return gdpr.isConsentGivenForPurposes(gdprConsentPurposes);
                            }
                        });
                    } else {
                        return true;
                    }
                })
            );
        }

        if (blockGppReject) {
            const cm = window.phxConsentManagement;
            if (!cm) {
                throw new Error('window.phxConsentManagement is required');
            }
            promises.push(cm.gppAccepted());
        }

        return promises.length
            ? Promise.all(promises).then((results) => results.every((v) => v))
            : Promise.resolve(true);
    }

    fetchWithBasePath(fetchPath, params) {
        return clientConfigPromise.then((config) => {
            if (config.siteBasePath) {
                fetchPath = config.siteBasePath + fetchPath;
            }
            return fetch(fetchPath, params);
        });
    }

    getCookieValue(name) {
        return getCookieValue(name);
    }

    removeCookie(name) {
        let domain = document.location.hostname;
        // Dependent on https://mavencorp.atlassian.net/browse/PA-418
        if (domain && domain.split('.').length > 2) {
            domain = domain.substring(domain.indexOf('.') + 1);
        }
        let params = {
            [name]: '',
            domain: `.${domain}`,
            expires: 'Thu, 01 Jan 1970 00:00:00 GMT',
            path: '/',
        };
        document.cookie = Object.keys(params)
            .map((k) => `${k}=${params[k]};`)
            .join('');
    }

    getKnownUser() {
        const self = this;
        function _getUserProfilePromise(taigaUrl) {
            const rgisCookieVal = self.RGISCookieValue;
            if (!rgisCookieVal || !taigaUrl) {
                return Promise.resolve({});
            }
            return fetch(`${taigaUrl}/api/user/profile`, {
                headers: {
                    'Content-Type': 'application/json',
                    'X-RGIS': rgisCookieVal,
                },
            }).then((resp) => {
                if (resp.status === 401) {
                    return {};
                }
                return resp.json();
            });
        }
        if (!window.phoenixKnownUserPromise) {
            window.phoenixKnownUserPromise = _getUserProfilePromise;
        }
        return clientConfigPromise.then((config) =>
            window.phoenixKnownUserPromise(config.taigaUrl).then((knownUser) => knownUser.userProfile)
        );
    }

    get RGISCookieValue() {
        const cookieVal = this.getCookieValue('RGIS');
        if (cookieVal?.startsWith('-') || cookieVal?.startsWith(`"-`)) {
            // These are *illegal* RGISes in the Phoenix code base that will confuse our
            // standalone-login state machine. (They were caused by logging out of the legacy
            // TheStreet websites (ContribDesk or Rosebud.) We will delete relevant cookies,
            // as though the user had logged out of Phoenix.
            this.removeCookie('RGIS');
            this.removeCookie('mct');
            this.removeCookie('mut');
            return '';
        }
        if (cookieVal) {
            // RGIS value may be in double quotes for some reason
            return cookieVal.replace(/^"/, '').replace(/"$/, '');
        }
        return '';
    }
}

/**
 * PhoenixPage
 *
 * This custom element is special, in addition of being a custom element on its own (the markup
 * of this element can be rendered server side with page level configuration as a JSON blob), it
 * is also a vehicule for shared pieces of code in the form of static methods.
 *
 * @example
 *   const { getDependencyClass } = require('../../lib/dependencies.js');
 *   getDependencyClass('phoenix-page').then(pageClass => {
 *       class MyWebComponent extends pageClass.PhoenixElement {
 *           // ...
 *       }
 *       window.customElements.define('my-web-component', MyWebComponent);
 *   }
 *
 * @example
 *   <phoenix-page config='{"json":"example"}'></phoenix-page>
 */
class PhoenixPage extends PhoenixElement {
    get observedEmittedEvents() {
        return [
            // phoenix-page being the root of the page, needs to register and
            // un-register all component listening to broadcasted events
            'register:broadcastListener',
            'unregister:broadcastListener',
            'phoenix:dropDown',
            'phoenix:overlay',
            'phoenix:toggle:className',
        ];
    }

    emittedEventCallback(name, event) {
        const callback = event.detail.callback || function () {};
        const eventType = event.detail.type;
        const eventValue = event.detail && event.detail.value;

        switch (name) {
            case 'register:broadcastListener':
                if (eventType) {
                    this.addEventListener(eventType, (e) => {
                        callback(eventType, e);
                    });
                }
                break;
            case 'unregister:broadcastListener':
                if (eventType) {
                    this.removeEventListener(eventType, (e) => {
                        callback(eventType, e);
                    });
                }
                break;

            case 'phoenix:dropDown':
                this.emitEvent('phoenix:dropDown:active', {
                    bubbles: true,
                    detail: {
                        key: 'phoenix:dropDown:active',
                        value: eventValue,
                    },
                });
                break;

            case 'phoenix:overlay':
                document.body.style.overflow = eventValue ? 'hidden' : '';
                break;

            // Example:
            // <phoenix-notifier
            //      event-name="phoenix:toggle:className"
            //      event-value="is-active"
            //      toggle-element-selector="{ElementSelector}"></phoenix-notifier>
            case 'phoenix:toggle:className':
                const toggleElementSelector = event.target.getAttr('toggle-element-selector');
                if (toggleElementSelector && eventValue) {
                    document.querySelectorAll(toggleElementSelector).forEach((el) => {
                        el.classList.toggle(eventValue);
                    });
                }
                break;
        }
    }

    /**
     * Expose the basicAuth module.
     */
    static get basicAuth() {
        return basicAuth;
    }

    /**
     * Expose the util module.
     */
    static get util() {
        return util;
    }

    /**
     * Expose the PhoenixElement base class.
     */
    static get PhoenixElement() {
        return PhoenixElement;
    }

    connectedCallback() {
        super.connectedCallback();
        this._addListener();
    }

    _addListener() {
        this.addEventListener('click', this.notifyPageClicked);
        this.observedBreakpoint = util.getCurrentBreakpoint();
        window.addEventListener('resize', this.notifyBreakpointChange.bind(this));
    }

    _removeListener() {
        this.removeEventListener('click', this.notifyPageClicked);
        window.removeEventListener('resize', this.notifyBreakpointChange.bind(this));
    }

    notifyPageClicked(e) {
        const eventName = 'phoenix:page:clicked';
        this.emitEvent(eventName, {
            bubbles: true,
            detail: {
                key: eventName,
                value: e,
            },
        });
    }

    notifyBreakpointChange() {
        const breakpoint = util.getCurrentBreakpoint();
        const eventName = 'phoenix:breakpoint:changed';

        if (breakpoint !== this.observedBreakpoint) {
            this.observedBreakpoint = breakpoint;
            this.emitEvent(eventName, {
                bubbles: true,
                detail: {
                    key: eventName,
                    value: breakpoint,
                },
            });
        }
    }

    disconnectedCallback() {
        super.disconnectedCallback();
        this._removeListener();
    }
}

window.customElements.define('phoenix-page', PhoenixPage);
measure.end();
